import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RefreshCcw, ScrollText } from "lucide-react";

const ACTION_LABELS = {
  "user.create": "Utente creato", "user.update": "Utente modificato",
  "user.password_reset": "Password resettata", "user.archive": "Utente archiviato",
  "user.unarchive": "Utente riattivato", "user.permissions_updated": "Permessi aggiornati",
  "category.create": "Categoria creata", "category.update": "Categoria modificata",
  "category.delete": "Categoria eliminata", "product.create": "Prodotto creato",
  "product.update": "Prodotto modificato", "product.availability": "Disponibilità prodotto modificata",
  "product.delete": "Prodotto eliminato", "menu.publish": "Menu pubblicato",
  "settings.update": "Impostazioni modificate", "order.cancel_reason": "Motivo annullamento",
  "order.reprint": "Ordine ristampato", "booking.created": "Prenotazione creata",
  "reservation.create": "Prenotazione creata", "reservation.update": "Prenotazione modificata",
  "reservation.status_change": "Stato prenotazione", "reservation.delete": "Prenotazione eliminata",
  "reservation.resend_confirmation": "Messaggio prenotazione rinviato",
  "customer.create": "Cliente creato", "customer.create_auto": "Cliente creato automaticamente",
  "customer.update": "Cliente modificato", "customer.anonymize": "Cliente anonimizzato",
  "customer.hard_delete": "Cliente eliminato", "staff.pin_login": "Accesso PIN",
  "http.post": "Operazione", "http.patch": "Modifica", "http.put": "Modifica", "http.delete": "Eliminazione",
};

const MODULES = [
  ["", "Tutti"], ["reservations", "Prenotazioni"], ["crm", "CRM"], ["whatsapp", "WhatsApp"],
  ["orders", "Ordini"], ["menu", "Prodotti e menu"], ["sala", "Sala"],
  ["staff_admin", "Staff e utenti"], ["settings", "Impostazioni"], ["auth", "Accessi"], ["other", "Altro"],
];
const OUTCOMES = [["", "Tutti"], ["success", "Completata"], ["accepted", "Richiesta eseguita"], ["no_change", "Nessuna modifica"], ["partial", "Salvata con errore"], ["denied", "Negata"], ["rejected", "Rifiutata"], ["error", "Errore"]];
const OUTCOME_STYLE = { success: "text-emerald-800", accepted: "text-ink-700", no_change: "text-ink-500", partial: "text-amber-800", denied: "text-rose-700", rejected: "text-amber-800", error: "text-rose-800" };
const OUTCOME_LABEL = Object.fromEntries(OUTCOMES.slice(1));
const AUTH_LABEL = { staff_pin: "Staff PIN", admin_password: "Admin", unknown: "Sessione" };
const EMPTY = { operator: "", module: "", outcome: "", action: "", entity_id: "", from_date: "", to_date: "" };

const romeTime = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", {
  timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric",
  hour: "2-digit", minute: "2-digit", second: "2-digit",
}) : "");

function Actor({ it }) {
  if (it.actor_type && it.actor_type !== "user") {
    return <span className="text-ink-600">{it.actor_type === "public_customer" ? "Cliente (QR/pubblico)" : "Sistema"}</span>;
  }
  return (
    <>
      <div className="font-medium">{it.user_name || it.user_email}</div>
      <div className="text-[11px] text-ink-500">{it.user_role}{it.auth_method ? ` · ${AUTH_LABEL[it.auth_method] || it.auth_method}` : ""}</div>
    </>
  );
}

function SelectFilter({ label, value, options, onChange, testid }) {
  return (
    <div><Label>{label}</Label>
      <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v || "all"} value={v || "all"}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export default function AdminAuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [offset, setOffset] = useState(0);
  const limit = 100;
  const upd = (k) => (v) => { setF((s) => ({ ...s, [k]: v })); setOffset(0); };

  const reqId = useRef(0);
  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v) { params.set(k, v); } });
      params.set("limit", limit); params.set("offset", offset);
      const { data } = await api.get(`/admin/audit-log?${params}`);
      if (id !== reqId.current) { return; }
      setItems(data.items); setTotal(data.total);
    } catch (e) { if (id === reqId.current) { toast.error(formatApiError(e)); } }
    finally { if (id === reqId.current) { setLoading(false); } }
  }, [f, offset]);

  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [load]);

  return (
    <div>
      <div className="eyebrow">Sicurezza</div>
      <div className="flex justify-between items-center mt-1">
        <h1 className="text-4xl flex items-center gap-2"><ScrollText className="w-8 h-8 text-bronze-600" strokeWidth={1.5} />Audit log</h1>
        <Button variant="outline" onClick={load} className="rounded-full" disabled={loading}
                data-testid="audit-refresh"><RefreshCcw className="w-4 h-4 mr-1" />Aggiorna</Button>
      </div>
      <p className="text-sm text-ink-500 mt-1">Registro immutabile delle operazioni aziendali, attribuite all'operatore autenticato. Orari in Europe/Rome.</p>

      <div className="card-elevated p-4 mt-5 grid gap-3 md:grid-cols-4 lg:grid-cols-8">
        <div><Label>Operatore</Label>
          <Input value={f.operator} onChange={(e) => upd("operator")(e.target.value)} data-testid="audit-operator" placeholder="nome o email" />
        </div>
        <SelectFilter label="Modulo" value={f.module} options={MODULES} onChange={upd("module")} testid="audit-module" />
        <SelectFilter label="Esito" value={f.outcome} options={OUTCOMES} onChange={upd("outcome")} testid="audit-outcome" />
        <div><Label>Operazione</Label>
          <Input value={f.action} onChange={(e) => upd("action")(e.target.value)} data-testid="audit-action" placeholder="es. status_change" />
        </div>
        <div><Label>Risorsa (ID)</Label>
          <Input value={f.entity_id} onChange={(e) => upd("entity_id")(e.target.value)} data-testid="audit-entity-id" />
        </div>
        <div><Label>Dal</Label>
          <Input type="date" value={f.from_date} onChange={(e) => upd("from_date")(e.target.value)} data-testid="audit-from" />
        </div>
        <div><Label>Al</Label>
          <Input type="date" value={f.to_date} onChange={(e) => upd("to_date")(e.target.value)} data-testid="audit-to" />
        </div>
        <div className="flex items-end"><Button variant="outline" onClick={() => { setF(EMPTY); setOffset(0); }} className="rounded-full w-full" data-testid="audit-reset">Azzera</Button></div>
      </div>

      <div className="text-xs text-ink-500 mt-3" data-testid="audit-total">{total} eventi totali · Mostrati {items.length}</div>

      <div className="card-elevated mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-ink-500 bg-sand-50">
            <tr>
              <th className="text-left p-2">Data/Ora</th><th className="text-left">Operatore</th>
              <th className="text-left">Modulo</th><th className="text-left">Operazione</th>
              <th className="text-left">Esito</th><th className="text-left">Risorsa</th><th className="text-left">Dettagli</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-sand-200 align-top" data-testid={`audit-row-${it.id}`}>
                <td className="p-2 whitespace-nowrap font-mono text-xs">{romeTime(it.at)}</td>
                <td className="whitespace-nowrap pr-2" data-testid={`audit-actor-${it.id}`}><Actor it={it} /></td>
                <td className="text-xs">{(MODULES.find(([k]) => k === it.module) || [, it.module || "—"])[1]}</td>
                <td className="whitespace-nowrap"><span className="chip text-[11px]">{ACTION_LABELS[it.action] || it.action}</span></td>
                <td className={`text-xs font-medium ${OUTCOME_STYLE[it.outcome] || ""}`} data-testid={`audit-outcome-${it.id}`}>{OUTCOME_LABEL[it.outcome] || it.outcome}</td>
                <td className="font-mono text-xs">{it.entity_type}<div className="text-ink-500">{it.entity_id?.slice(-8) || "-"}</div></td>
                <td className="text-xs max-w-md">
                  {it.metadata && <div className="break-all"><b>info:</b> {JSON.stringify(it.metadata).slice(0, 200)}</div>}
                  {it.previous && <div className="text-ink-500 break-all"><b>da:</b> {JSON.stringify(it.previous).slice(0, 160)}</div>}
                  {it.new_value && <div className="break-all"><b>a:</b> {JSON.stringify(it.new_value).slice(0, 160)}</div>}
                </td>
              </tr>
            ))}
            {items.length === 0 && !loading && <tr><td colSpan="7" className="p-8 text-center text-ink-500">Nessun evento registrato.</td></tr>}
          </tbody>
        </table>
      </div>

      {total > limit && (
        <div className="mt-3 flex items-center justify-between">
          <Button variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Precedenti</Button>
          <div className="text-xs text-ink-500">{offset + 1} – {Math.min(offset + limit, total)} di {total}</div>
          <Button variant="outline" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>Successivi</Button>
        </div>
      )}
    </div>
  );
}
